import { Button } from "reactstrap";
import { Dispatch, SetStateAction, useState } from "react";
import IconButton from "../../../elements/IconButton";
import AudioRecorder from "../../Sidebar/QuickNote/QuickNoteRecording/AudioRecordingUtils";
import useTranscript from "../../../api/api-hooks/tools/useTranscript";

interface Props {
  isRecording: boolean;
  setIsRecording: Dispatch<SetStateAction<boolean>>;
  isProcessingRecording: boolean;
  setIsProcessingRecording: Dispatch<SetStateAction<boolean>>;
  handleSendMessage: (providedMessage?: string) => void;
}

const Recorder = new AudioRecorder();

export default function BrieGPTVoiceRecorder({
  isRecording,
  setIsRecording,
  isProcessingRecording,
  setIsProcessingRecording,
  handleSendMessage,
}: Props) {
  const [error, setError] = useState("");
  const { fetchTranscript, deleteTranscript } = useTranscript();

  if (isProcessingRecording) {
    return (
      <div className="tw:flex tw:items-center text-nowrap tw:mr-8">
        <div className="tw:text-[13px] flow-text tw:font-medium">
          <span className="flow-text-inner">Processing voice...</span>
        </div>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div className="tw:flex tw:items-center text-nowrap">
        <div className="tw:text-[13px] flow-text tw:font-medium">
          <span className="flow-text-inner">Brie is listening...</span>
        </div>
        <IconButton
          icon="stop"
          color="primary"
          onClick={onFinishRecording}
          className="ml-2"
          size="lg"
        />
      </div>
    );
  }

  return (
    <>
      <Button
        onClick={onStartRecording}
        outline
        color="primary"
        size="sm"
        className="tw:flex tw:items-center tw:pl-2 tw:lg:pl-0 tw:lg:pr-2 tw:h-[39px] tw:rounded-lg"
      >
        <div className="tw:px-1">
          <i className="mdi mdi-microphone tw:text-2xl tw:mt-4" />
        </div>
        <span className="tw:hidden tw:lg:block">Voice Chat</span>
      </Button>
    </>
  );

  function restart() {
    setIsRecording(false);
    setIsProcessingRecording(false);
    Recorder.cancelRecording();
  }

  function onStartRecording() {
    Recorder.startRecording({
      setError,
      setIsRecording,
      restart,
      onSuccess: (audio) => {
        onFetchTranscript(audio);
      },
    });
  }

  function onFinishRecording() {
    setIsRecording(false);
    setIsProcessingRecording(true);
    Recorder.finishRecording({});
  }

  // Fetches a transcript from the audio, calls process request on success.
  async function onFetchTranscript(audioBlob: Blob) {
    try {
      const transcriptFileData = await fetchTranscript({
        audioBlob,
        onTranscriptSuccess: (transcript, fileData) => {
          // Send transcript to BrieGPT
          handleSendMessage(transcript);

          // Delete transcript's temporary file
          deleteTranscript({ fileData: fileData });
        },
        onError: (fileData) => {
          setError("Something went wrong...");
          deleteTranscript({ fileData: fileData });
        },
      });

      deleteTranscript({
        fileData: transcriptFileData,
      });
    } catch (err) {
    } finally {
      setIsProcessingRecording(false);
    }
  }
}
